import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getProducts } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/motion";
import { Gift, RotateCcw } from "lucide-react";

const STEPS = [
  {
    key: "who", title: "Kim için?", options: [
      { id: "kendim", label: "Kendim" },
      { id: "partner", label: "Partnerim" },
      { id: "anne", label: "Annem" },
      { id: "arkadas", label: "Arkadaşım" },
    ],
  },
  {
    key: "moment", title: "Hangi an için?", options: [
      { id: "dogum-gunu", label: "Doğum Günü" },
      { id: "yildonumu", label: "Yıldönümü" },
      { id: "yeni-is", label: "Yeni İş" },
      { id: "kutlama", label: "Özel Kutlama" },
      { id: "sadece", label: "Sadece" },
    ],
  },
  {
    key: "budget", title: "Bütçen ne?", options: [
      { id: "600", label: "600 TL altı" },
      { id: "1200", label: "600 – 1.200 TL" },
      { id: "9999", label: "1.200 TL üzeri" },
    ],
  },
];

export default function GiftFinder() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ who: null, moment: null, budget: null });
  const [results, setResults] = useState(null);
  const [relaxed, setRelaxed] = useState(false);
  const [loading, setLoading] = useState(false);

  const prefill = useMemo(() => {
    const who = params.get("who");
    const moment = params.get("moment");
    const budget = params.get("budget");
    return who && moment && budget ? { who, moment, budget } : null;
  }, [params]);

  const search = async (a) => {
    setLoading(true);
    try {
      const q = { gift_for: a.who, gift_moment: a.moment, limit: 8 };
      if (a.budget === "600") q.max_price = 600;
      else if (a.budget === "1200") { q.min_price = 600; q.max_price = 1200; }
      else q.min_price = 1200;
      const r = await getProducts(q);
      setRelaxed(false);
      setResults(r);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (prefill) {
      setAnswers(prefill);
      search(prefill);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const pick = (key, id) => {
    const next = { ...answers, [key]: id };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else search(next);
  };

  const reset = () => {
    setAnswers({ who: null, moment: null, budget: null });
    setStep(0);
    setResults(null);
    setRelaxed(false);
  };

  return (
    <>
      <Seo title="Hediye Bulucu" description="Üç soruda sevdiklerin için doğru hediyeyi bul." path="/hediye" />
      <div className="container-shule py-12 lg:py-20">
        <Reveal className="text-center max-w-xl mx-auto">
          <span className="inline-flex w-14 h-14 rounded-full bg-shule-goldlight/50 border border-shule-gold/30 items-center justify-center">
            <Gift className="w-6 h-6 text-shule-goldhover" />
          </span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-shule-coffee">Hediye Bulucu</h1>
          <p className="mt-3 text-shule-mocha">Kimin için, hangi an için ve bütçeni seç; mevcut ürünler arasından eşleşenleri gösterelim.</p>
        </Reveal>

        {results === null ? (
          <Reveal delay={0.1} className="mt-12 max-w-2xl mx-auto rounded-[2rem] border border-shule-line bg-shule-paper p-6 sm:p-10 shadow-[0_10px_30px_-10px_rgba(61,46,40,0.05)]">
            <div className="flex gap-2 mb-8">
              {STEPS.map((s, i) => (
                <div key={s.key} data-testid={`gift-progress-${i}`} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-shule-gold" : "bg-shule-line"}`} />
              ))}
            </div>
            <p className="eyebrow mb-1">Adım {step + 1} / {STEPS.length}</p>
            <h2 className="font-serif text-3xl mb-6">{STEPS[step].title}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {STEPS[step].options.map((o) => (
                <button key={o.id} data-testid={`gift-step-${step + 1}-${o.id}`} onClick={() => pick(STEPS[step].key, o.id)}
                  className={`min-h-[56px] px-5 rounded-2xl border text-left font-medium transition-all duration-300 hover:border-shule-gold hover:shadow-[0_10px_25px_-10px_rgba(197,160,89,0.35)] ${answers[STEPS[step].key] === o.id ? "bg-shule-coffee text-shule-paper border-shule-coffee" : "border-shule-lineaccent"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button data-testid="gift-back-button" onClick={() => setStep(step - 1)} className="mt-6 text-sm font-semibold text-shule-soft hover:text-shule-goldhover min-h-[44px]">
                ← Önceki adım
              </button>
            )}
          </Reveal>
        ) : (
          <div className="mt-12">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 data-testid="gift-results-title" className="font-serif text-3xl text-shule-coffee">
                {loading ? "Senin için seçiyoruz…" : relaxed ? `${results.length} yakın eşleşme` : `${results.length} tam eşleşme`}
              </h2>
              <button data-testid="gift-restart-button" onClick={reset} className="btn-outline-shule !px-5">
                <RotateCcw className="w-4 h-4" /> Baştan Başla
              </button>
            </div>
            {results.length === 0 && !loading ? (
              <div className="text-center py-16">
                <p className="text-shule-soft font-serif text-2xl">Bu üç kritere tam uyan ürün bulunamadı.</p>
                <button
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const q = { gift_for: answers.who, limit: 8 };
                      if (answers.budget === "600") q.max_price = 600;
                      else if (answers.budget === "1200") { q.min_price = 600; q.max_price = 1200; }
                      else q.min_price = 1200;
                      const r = await getProducts(q);
                      setRelaxed(true);
                      setResults(r);
                    } catch {
                      setResults([]);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="btn-outline-shule mt-6"
                >
                  Aynı kişi ve bütçede diğer seçenekleri göster
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                {results.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
